import React, { useEffect, useMemo, useRef } from "react";
import "./JsonEditor.css";

type JsonEditorProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
};

/**
 * Escape HTML entities to prevent XSS attacks.
 * SECURITY: This MUST be called on raw input BEFORE any highlighting.
 * Prevents injection of malicious HTML/JS through JSON content.
 */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Syntax highlighting for JSON content.
 *
 * SECURITY ANALYSIS:
 * 1. escapeHtml() is called FIRST - all < > & are escaped, preventing HTML/script injection
 * 2. The tokenRegex only matches specific JSON patterns (strings, bools, null, numbers)
 * 3. The replacement only wraps matches in <span> tags with hardcoded CSS class names
 * 4. The matched content comes from escaped input, so it cannot contain executable code
 *
 * This is safe because malicious input like <script>alert(1)</script> becomes
 * &lt;script&gt;alert(1)&lt;/script&gt; BEFORE the regex runs.
 */
const highlightJson = (value: string): string => {
  if (!value) {
    return "";
  }
  // CRITICAL: Escape all HTML entities first to prevent XSS
  const escaped = escapeHtml(value);

  // Regex matches JSON tokens. Safe because input is already HTML-escaped.
  const tokenRegex =
    /("(?:\\.|[^"\\])*"(?::)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/gi;

  return escaped.replace(tokenRegex, (match) => {
    let tokenClass = "json-editor__token--number";
    if (match.startsWith("\"")) {
      if (match.endsWith(":")) {
        const key = match.slice(0, -1);
        return `<span class="json-editor__token json-editor__token--key">${key}</span>:`;
      }
      tokenClass = "json-editor__token--string";
    } else if (/true|false/i.test(match)) {
      tokenClass = "json-editor__token--boolean";
    } else if (/null/i.test(match)) {
      tokenClass = "json-editor__token--null";
    }
    return `<span class="json-editor__token ${tokenClass}">${match}</span>`;
  });
};

const JsonEditor: React.FC<JsonEditorProps> = ({
  id,
  value,
  onChange,
  disabled = false,
  rows = 8,
  placeholder,
}) => {
  const highlighted = useMemo(() => highlightJson(value), [value]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const codeRef = useRef<HTMLPreElement | null>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    if (codeRef.current) {
      codeRef.current.style.height = el.style.height;
    }
  }, [value]);

  return (
    <div className={`json-editor ${disabled ? "is-disabled" : ""}`}>
      <pre
        className="json-editor__code"
        ref={codeRef}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
      <textarea
        id={id}
        className="json-editor__textarea"
        ref={textareaRef}
        value={value}
        spellCheck={false}
        rows={rows}
        disabled={disabled}
        onChange={(event) => {
          if (!disabled) {
            onChange(event.target.value);
          }
        }}
      />
      {!value.trim() && placeholder && (
        <span className="json-editor__placeholder">{placeholder}</span>
      )}
    </div>
  );
};

export default JsonEditor;
